import "./global.css";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { lazy, Suspense } from "react";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";

// Critical routes — loaded eagerly (homepage, browse, spa profile)
import Index from "./pages/Index";
import Browse from "./pages/Browse";
import SpaProfile from "./pages/SpaProfile";
import NotFound from "./pages/NotFound";

// Non-critical routes — lazy loaded
const MapPage = lazy(() => import("./pages/MapPage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ClaimHelp = lazy(() => import("./pages/ClaimHelp"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const StateBrowse = lazy(() => import("./pages/StateBrowse"));
const CityBrowse = lazy(() => import("./pages/CityBrowse"));
const CityTreatmentBrowse = lazy(() => import("./pages/CityTreatmentBrowse"));
const CityBestBrowse = lazy(() => import("./pages/CityBestBrowse"));
const StateTreatmentBrowse = lazy(() => import("./pages/StateTreatmentBrowse"));
const Guides = lazy(() => import("./pages/Guides"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const CostByCityGuide = lazy(() => import("./pages/CostByCityGuide"));
const BestProvidersByTreatment = lazy(() => import("./pages/BestProvidersByTreatment"));
const Treatments = lazy(() => import("./pages/Treatments"));
const TreatmentPage = lazy(() => import("./pages/TreatmentPage"));
const Placeholder = lazy(() => import("./pages/Placeholder"));
const WhatIsAMedSpa = lazy(() => import("./pages/WhatIsAMedSpa"));

// Dashboard routes — lazy loaded (auth-gated, never needed on initial load)
const DashboardLayout = lazy(() => import("./pages/dashboard/DashboardLayout"));
const MyProfile = lazy(() => import("./pages/dashboard/MyProfile"));
const MyListings = lazy(() => import("./pages/dashboard/MyListings"));
const EditListing = lazy(() => import("./pages/dashboard/EditListing"));
const AddListing = lazy(() => import("./pages/dashboard/AddListing"));
const UpgradePlan = lazy(() => import("./pages/dashboard/UpgradePlan"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/spa/:slug" element={<SpaProfile />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/browse/:stateSlug" element={<StateBrowse />} />
            <Route path="/browse/:stateSlug/treatments/:treatmentSlug" element={<StateTreatmentBrowse />} />
            <Route path="/browse/:stateSlug/:citySlug" element={<CityBrowse />} />
            <Route path="/browse/:stateSlug/:citySlug/best" element={<CityBestBrowse />} />
            <Route path="/browse/:stateSlug/:citySlug/:treatmentSlug" element={<CityTreatmentBrowse />} />
            <Route path="/treatments" element={<Treatments />} />
            <Route path="/treatments/:treatmentSlug" element={<TreatmentPage />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/cost-by-city/:treatmentSlug" element={<CostByCityGuide />} />
            <Route path="/best/:treatmentSlug" element={<BestProvidersByTreatment />} />
            <Route path="/guides/:guideSlug" element={<GuidePage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim-help" element={<ClaimHelp />} />
            <Route path="/what-is-a-medspa" element={<WhatIsAMedSpa />} />
            <Route path="/med-spa-directories-compared-why-medspa-directory-pro-stands-out" element={<Navigate to="/blog/med-spa-directories-compared-why-medspa-directory-pro-stands-out" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<MyProfile />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="listings" element={<MyListings />} />
              <Route path="listings/new" element={<AddListing />} />
              <Route path="listings/:id" element={<EditListing />} />
              <Route path="upgrade" element={<UpgradePlan />} />
              <Route path="admin" element={<AdminDashboard />} />
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
    </Suspense>
  );
}

export default function App({ queryClient }: { queryClient: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppRoutes />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
